import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[90vw] max-h-[90vh]',
};

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        className={`
          relative bg-white rounded-xl shadow-xl
          ${sizeClasses[size]}
          w-full max-h-[90vh]
          ${size === 'full' ? 'overflow-hidden flex flex-col' : 'overflow-auto'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between p-6 border-b border-gray-200 bg-white z-10 flex-shrink-0">
          {title && (
            <h2 className="text-xl font-semibold text-gray-900 pr-4 min-w-0 break-words">
              {title}
            </h2>
          )}
          <button
            onClick={onClose}
            className="ml-auto p-2 text-gray-400 hover:text-gray-600 focus-ring rounded-md flex-shrink-0"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className={size === 'full' ? 'flex-1 min-h-0 overflow-hidden flex flex-col' : 'p-6'}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
